import * as React from "react";
import { Button, LargeTitle, makeStyles, Text } from "@fluentui/react-components";
import {
    Apps24Regular,
    Sparkle24Regular,
    Shield24Regular,
    BrainCircuit24Regular,
    ChevronRight20Regular,
} from "@fluentui/react-icons";
import IHeroProps from "./IHeroProps";
import TrustStrip, { TRUST_STRIP_BG } from "./TrustStrip";
import { dolphinTokens } from "../../utils/Theme";

export interface IHeroCardItem {
    icon: React.ReactElement;
    title: string;
    subtitle: string;
    tone?: "blue" | "peach" | "mint" | "lavender";
    href?: string;
}

interface IHeroStaticProps extends Partial<IHeroProps> {
    topBadgeText?: string;
    cardItems?: IHeroCardItem[];
}

function useTopOffset<T extends HTMLElement>() {
    const ref = React.useRef<T>(null);
    const [offset, setOffset] = React.useState(0);

    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const measure = () => {
            setOffset(Math.round(el.getBoundingClientRect().top + window.scrollY));
        };

        measure();
        window.addEventListener("resize", measure);
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
        observer?.observe(document.body);

        return () => {
            window.removeEventListener("resize", measure);
            observer?.disconnect();
        };
    }, []);

    return [ref, offset] as const;
}

const useStyles = makeStyles({
    heroSection: {
        height: "calc(100svh - var(--hero-offset, 0px))",
        minHeight: "calc(100svh - var(--hero-offset, 0px))",
        display: "flex",
        flexDirection: "column",
    },
    hero: {
        position: "relative",
        boxSizing: "border-box",
        flex: "1 1 0",
        minHeight: 0,
        padding: "4rem 2rem 6rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: dolphinTokens.colorDarkBlue,
        backgroundImage: `radial-gradient(ellipse 50% 75% at 78% 45%, ${dolphinTokens.colorLightBlue40}, transparent 70%)`,
        color: dolphinTokens.colorNeutralForegroundOnBrand,
        overflow: "hidden",
        "@media (max-width: 980px)": {
            padding: "1.5rem 1.25rem 3rem",
        },
    },
    heroContent: {
        position: "relative",
        zIndex: 1,
        display: "grid",
        gridTemplateColumns: "1.15fr 0.85fr",
        gap: "3rem",
        width: "100%",
        maxWidth: "1400px",
        alignItems: "center",
        "@media (max-width: 980px)": {
            gridTemplateColumns: "1fr",
            gap: "2rem",
        },
    },
    leftColumn: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        maxWidth: "740px",
        "@media (max-width: 980px)": {
            gap: "0.75rem",
            margin: "0 auto",
        },
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "999px",
        backgroundColor: dolphinTokens.colorLightBlue40,
        color: dolphinTokens.colorNeutralForegroundOnBrand,
        padding: "0.65rem 1rem",
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        fontWeight: 700,
        fontSize: "0.78rem",
        width: "fit-content",
        whiteSpace: "nowrap",
        "@media (max-width: 980px)": {
            alignSelf: "center",
        },
    },
    title: {
        color: dolphinTokens.colorNeutralForegroundOnBrand,
        fontSize: "4rem",
        lineHeight: 1.05,
        maxWidth: "680px",
        marginTop: "0.2rem",
        marginBottom: "0.5rem",
        "@media (max-width: 980px)": {
            fontSize: "2.5rem",
            textAlign: "center",
        },
    },
    subtitle: {
        color: dolphinTokens.colorNeutralForegroundOnBrand,
        opacity: 0.88,
        fontSize: "1.03rem",
        lineHeight: 1.8,
        maxWidth: "620px",
        marginBottom: "1.5rem",
        "@media (max-width: 980px)": {
            textAlign: "center",
            marginBottom: "0.5rem",
        },
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        gap: "1rem",
        alignItems: "center",
        "@media (max-width: 980px)": {
            justifyContent: "center",
        },
    },
    primaryButton: {
        minWidth: "180px",
        padding: "0.95rem 1.6rem",
        borderRadius: "999px",
        transitionProperty: "transform, box-shadow",
        transitionDuration: "220ms",
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
        ":hover": {
            transform: "translateY(-3px)",
            boxShadow: `0 14px 28px ${dolphinTokens.colorLightBlue40}`,
        },
        ":active": {
            transform: "translateY(-1px) scale(0.97)",
            transitionDuration: "100ms",
        },
        ":hover:active": {
            transform: "translateY(-1px) scale(0.97)",
        },
    },
    secondaryButton: {
        minWidth: "180px",
        padding: "0.95rem 1.6rem",
        borderRadius: "999px",
        transitionProperty: "transform, box-shadow",
        transitionDuration: "220ms",
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
        ":hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 14px 28px rgba(0, 0, 0, 0.25)",
        },
        ":active": {
            transform: "translateY(-1px) scale(0.97)",
            transitionDuration: "100ms",
        },
        ":hover:active": {
            transform: "translateY(-1px) scale(0.97)",
        },
    },
    logosRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.8rem",
        alignItems: "center",
        marginTop: "0.5rem",
        justifyContent: "flex-start",
        "@media (max-width: 980px)": {
            justifyContent: "center",
        },
    },
    brandBadge: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: dolphinTokens.colorLightBlue20,
        border: `1px solid ${dolphinTokens.colorLightBlue40}`,
        color: dolphinTokens.colorNeutralForegroundOnBrand,
        borderRadius: "999px",
        padding: "0.6rem 1rem",
        fontWeight: 600,
        fontSize: "0.95rem",
    },

    // ---- right-hand card ----
    visual: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        "@media (max-width: 980px)": {
            display: "none",
        },
    },
    card: {
        width: "100%",
        maxWidth: "400px",
        padding: "1.5rem 2rem",
        borderRadius: "1.25rem",
        backgroundColor: dolphinTokens.colorNeutralBackground1,
        boxShadow: "0 30px 80px rgba(0, 20, 30, 0.45)",
    },
    cardRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.9rem",
        padding: "0.95rem 0",
        borderBottom: `1px solid ${dolphinTokens.colorDarkBlue20}`,
        textDecorationLine: "none",
        color: dolphinTokens.colorDarkBlue60,
        transitionProperty: "color",
        transitionDuration: "150ms",
        ":last-child": {
            borderBottom: "none",
        },
    },
    cardRowLink: {
        cursor: "pointer",
        ":hover": {
            color: dolphinTokens.colorLightBlue,
        },
    },
    cardChevron: {
        marginLeft: "auto",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        fontSize: "1.25rem",
        color: "inherit",
    },
    cardIcon: {
        width: "42px",
        height: "42px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    toneBlue: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: dolphinTokens.colorNeutralForegroundOnBrand,
    },
    tonePeach: {
        backgroundColor: dolphinTokens.colorPeachOrange,
        color: dolphinTokens.colorDarkBlue,
    },
    toneMint: {
        backgroundColor: dolphinTokens.colorMintGreen,
        color: dolphinTokens.colorDarkBlue,
    },
    toneLavender: {
        backgroundColor: dolphinTokens.colorSkyLavender,
        color: dolphinTokens.colorDarkBlue,
    },
    cardText: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },
    cardTitle: {
        fontSize: "0.95rem",
        fontWeight: 600,
        color: dolphinTokens.colorDarkBlue,
        lineHeight: 1.3,
    },
    cardSub: {
        fontSize: "0.8rem",
        color: dolphinTokens.colorDarkBlue60,
        marginTop: "2px",
    },

    // ---- wave ----
    wave: {
        position: "absolute",
        left: 0,
        bottom: "-1px",
        width: "100%",
        height: "clamp(48px, 7vw, 96px)",
        display: "block",
        pointerEvents: "none",
        zIndex: 2,
    },
    waveBack: {
        fill: dolphinTokens.colorLightBlue60,
    },
    waveFront: {
        fill: TRUST_STRIP_BG,
    },
});

const defaultCardItems: IHeroCardItem[] = [
    {
        icon: <Apps24Regular />,
        title: "Cloud transformation",
        subtitle: "Migration, modernisation and Microsoft 365",
        tone: "blue",
        href: "/services/cloud",
    },
    {
        icon: <Sparkle24Regular />,
        title: "Business process automation",
        subtitle: "Custom apps and workflows",
        tone: "peach",
        href: "/services/business-process-solutions",
    },
    {
        icon: <Shield24Regular />,
        title: "Security & compliance",
        subtitle: "Audits and risk reviews",
        tone: "mint",
        href: "/services/security",
    },
    {
        icon: <BrainCircuit24Regular />,
        title: "AI adoption & development",
        subtitle: "Strategy, solutions and enablement",
        tone: "lavender",
        href: "/ai/ai-adoption",
    },
];

export default function HeroStatic({
    title,
    subtitle,
    cta,
    secondaryCta,
    topBadgeText = "Digital Workplace Consultancy",
    cardItems = defaultCardItems,
}: IHeroStaticProps = {}) {
    const styles = useStyles();
    const [sectionRef, headerOffset] = useTopOffset<HTMLDivElement>();

    const toneClass = {
        blue: styles.toneBlue,
        peach: styles.tonePeach,
        mint: styles.toneMint,
        lavender: styles.toneLavender,
    };

    const sectionStyle = { "--hero-offset": `${headerOffset}px` } as React.CSSProperties;

    return (
        <div ref={sectionRef} className={styles.heroSection} style={sectionStyle}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <div className={styles.leftColumn}>
                        <span className={styles.badge}>{topBadgeText}</span>
                        <LargeTitle block as="h1" className={styles.title}>
                            {title}
                        </LargeTitle>
                        <Text block as="p" className={styles.subtitle} size={500}>
                            {subtitle}
                        </Text>
                        <div className={styles.actions}>
                            {cta && (
                                <Button appearance="primary" className={styles.primaryButton} as="a" href={cta.url}>
                                    {cta.text}
                                </Button>
                            )}
                            {secondaryCta && (
                                <Button appearance="secondary" className={styles.secondaryButton} as="a" href={secondaryCta.url}>
                                    {secondaryCta.text}
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className={styles.visual}>
                        <div className={styles.card}>
                            {cardItems.map((item) => {
                                const Row = item.href ? "a" : "div";
                                return (
                                    <Row
                                        key={item.title}
                                        className={item.href ? `${styles.cardRow} ${styles.cardRowLink}` : styles.cardRow}
                                        {...(item.href ? { href: item.href } : {})}
                                    >
                                        <div className={`${styles.cardIcon} ${toneClass[item.tone ?? "blue"]}`}>
                                            {item.icon}
                                        </div>
                                        <div className={styles.cardText}>
                                            <span className={styles.cardTitle}>{item.title}</span>
                                            <span className={styles.cardSub}>{item.subtitle}</span>
                                        </div>
                                        {item.href && (
                                            <span className={styles.cardChevron} aria-hidden="true">
                                                <ChevronRight20Regular />
                                            </span>
                                        )}
                                    </Row>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <svg
                    className={styles.wave}
                    viewBox="0 0 1440 72"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                    focusable="false"
                >
                    <path className={styles.waveBack} d="M0 16C260 -8 500 34 760 24C1020 14 1200 -8 1440 4V72H0V16Z" />
                    <path className={styles.waveFront} d="M0 32C240 72 480 0 720 36C960 72 1200 12 1440 40V72H0V32Z" />
                </svg>
            </section>
            <TrustStrip />
        </div>
    );
}